import { AlertCircle, CheckCircle, Clock, FileText, XCircle, type LucideIcon } from 'lucide-react';
import type * as React from 'react';
import { cn } from '../lib/cn.js';
import { Badge } from './badge.js';

/**
 * Les six statuts visuels du produit. C'est l'unique source des couleurs de
 * statut : aucun écran ne redéfinit sa propre table.
 */
export type StatusType = 'success' | 'pending' | 'error' | 'info' | 'scheduled' | 'draft';

export type StatusBadgeSize = 'sm' | 'default' | 'lg';

interface StatusStyle {
  icon: LucideIcon;
  className: string;
}

const STATUS_STYLES: Record<StatusType, StatusStyle> = {
  success: {
    icon: CheckCircle,
    className: 'bg-success/10 text-success ring-1 ring-inset ring-success/20 hover:bg-success/15',
  },
  pending: {
    icon: Clock,
    className: 'bg-warning/10 text-warning ring-1 ring-inset ring-warning/20 hover:bg-warning/15',
  },
  error: {
    icon: XCircle,
    className:
      'bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20 hover:bg-destructive/15',
  },
  info: {
    icon: AlertCircle,
    className: 'bg-info/10 text-info ring-1 ring-inset ring-info/20 hover:bg-info/15',
  },
  scheduled: {
    icon: Clock,
    className: 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/15',
  },
  draft: {
    icon: FileText,
    className: 'bg-muted text-muted-foreground ring-1 ring-inset ring-border hover:bg-muted/80',
  },
};

const SIZE_CLASSES: Record<StatusBadgeSize, string> = {
  sm: 'text-xs px-2 py-0.5',
  default: 'text-xs px-2 py-0.5',
  lg: 'text-sm px-2.5 py-1',
};

const ICON_SIZE_CLASSES: Record<StatusBadgeSize, string> = {
  sm: 'h-3 w-3',
  default: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

export interface StatusBadgeProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  status: StatusType;
  /** Libellé affiché. Toujours fourni par l'appelant, issu des traductions. */
  label: React.ReactNode;
  showIcon?: boolean;
  size?: StatusBadgeSize;
}

export function StatusBadge({
  status,
  label,
  showIcon = true,
  size = 'default',
  className,
  ...props
}: StatusBadgeProps) {
  const style = STATUS_STYLES[status];
  const Icon = style.icon;

  return (
    <Badge
      variant="secondary"
      className={cn(
        style.className,
        SIZE_CLASSES[size],
        'font-medium transition-colors duration-200',
        className,
      )}
      {...props}
    >
      {showIcon && <Icon className={cn(ICON_SIZE_CLASSES[size], 'mr-1')} />}
      {label}
    </Badge>
  );
}
